import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Radar, Upload, Send, Loader, Lightbulb, Plus, TrendingUp, DollarSign, ShoppingBag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const OpportunityRadar = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.radar;
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);
  const [view, setView] = useState('start');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [dataText, setDataText] = useState('');
  const [radarData, setRadarData] = useState(null);
  
  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Opportunity Radar', lastAssistant);
  const headers = { Authorization: `Bearer ${token}` };

  const uploadData = async (file) => {
    if (!file) return;
    setLoading(true); setView('result');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/compass/radar/upload-data?language=${lang}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setSessionId(res.data.session_id); setRadarData(res.data.radar_data);
      setMessages([{ role: 'user', content: `Uploaded ${file.name} (${res.data.rows} rows)` }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: (lang === 'en' ? 'Upload failed: ' : 'Upload mislukt: ') + (e.response?.data?.detail || e.message) }]); }
    finally { setLoading(false); }
  };

  const analyzeText = async () => {
    if (!((dataText) == null ? '' : String(dataText)).trim()) return;
    setLoading(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/radar/analyze-text`, { data_text: dataText, language: lang }, { headers });
      setSessionId(res.data.session_id); setRadarData(res.data.radar_data);
      setMessages([{ role: 'user', content: dataText }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Analysis failed. Please check your API configuration.' : 'Analyse mislukt. Controleer je API-configuratie.' }]); }
    finally { setLoading(false); }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || loading || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]); setInput(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/compass/radar/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Error. Please try again.' : 'Fout. Probeer het opnieuw.' }]); }
    finally { setLoading(false); }
  };

  const startNew = () => { setView('start'); setMessages([]); setSessionId(null); setDataText(''); setRadarData(null); };

  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><Radar size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="auto-start-section" data-testid="radar-start">
            <div className="auto-input-card">
              <div className="auto-input-header"><Radar size={20} /><h3 className="heading-3">{lang === 'nl' ? 'Ontdek Groeikansen' : 'Discover Growth Opportunities'}</h3></div>
              <p className="body-small text-secondary">{lang === 'nl' ? 'Upload verkoop/productdata of beschrijf je bedrijfscijfers om verborgen kansen te vinden.' : 'Upload sales/product data or describe your business metrics to find hidden opportunities.'}</p>
              <div className="explorer-dropzone" style={{ margin: '1rem 0', padding: '1.5rem' }} onClick={() => fileRef.current?.click()} data-testid="radar-dropzone">
                <Upload size={24} /><span className="body-small">{tp.uploadData}</span>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => uploadData(e.target.files[0])} />
              </div>
              <div style={{ textAlign: 'center', margin: '0.5rem 0', color: 'var(--text-secondary)' }}>{tp.orDescribe}</div>
              <textarea className="form-input auto-textarea" placeholder={tp.analyzePlaceholder} value={dataText} onChange={(e) => setDataText(e.target.value)} rows={5} data-testid="radar-textarea" />
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={analyzeText} disabled={!((dataText) == null ? '' : String(dataText)).trim()} data-testid="radar-analyze-btn">
                <Radar size={16} /> {tp.analyzeBtn}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compass-coach-page" data-testid="radar-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        <div className="compass-role-badge" style={{ background: 'var(--accent-green-200)' }}><Radar size={16} /><span className="body-small" style={{ fontWeight: 500 }}>{tp.title}</span></div>
        {radarData?.opportunities && (
          <div className="sim-sidebar-stats" data-testid="radar-stats">
            <div className="sim-stat-item"><TrendingUp size={14} /><span className="body-small">{lang === 'nl' ? 'Kansen' : 'Opportunities'}</span><span className="body-small" style={{ fontWeight: 600 }}>{radarData.opportunities.length}</span></div>
            {radarData.quick_wins && <div className="sim-stat-item"><Lightbulb size={14} /><span className="body-small">{lang === 'nl' ? 'Quick Wins' : 'Quick Wins'}</span><span className="body-small" style={{ fontWeight: 600 }}>{radarData.quick_wins.length}</span></div>}
          </div>
        )}
        <button className="compass-new-chat" onClick={startNew}><Plus size={14} /> {lang === 'nl' ? 'Nieuwe Scan' : 'New Scan'}</button>
      </aside>
      <main className="compass-main">
        <div className="compass-chat-container">
          {radarData?.opportunities && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem', padding: '1.25rem 0 0' }} data-testid="radar-cards">
              {radarData.opportunities.slice(0, 4).map((o, i) => (
                <div key={i} className="sim-outcome-card" style={{ borderTopColor: 'var(--accent-green-200)' }}>
                  <span className="caption">{o.type?.toUpperCase()}</span>
                  <span className="body-small" style={{ fontWeight: 600 }}>{o.title}</span>
                  {o.potential_impact && <span className="body-small text-secondary">{lang === 'nl' ? 'Impact' : 'Impact'}: {o.potential_impact}</span>}
                  <span className={`compass-difficulty compass-diff-${o.confidence?.toLowerCase() === 'high' ? 'beginner' : 'intermediate'}`} style={{ alignSelf: 'flex-start' }}>{o.confidence}</span>
                </div>
              ))}
            </div>
          )}
          <div className="compass-chat-messages" data-testid="radar-messages">
            {loading && messages.length === 0 && <div className="auto-analyzing-state"><Loader className="spin" size={32} /><h3 className="heading-3">{lang === 'nl' ? 'Scannen naar kansen...' : 'Scanning for opportunities...'}</h3></div>}
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div> : <div className="compass-msg-content">{msg.content}</div>}
              </div>
            ))}
            {loading && messages.length > 0 && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {lang === 'nl' ? 'Scannen...' : 'Scanning...'}</div></div>}
            <div ref={chatEndRef} />
          </div>
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tp.chatPlaceholder} disabled={loading} className="form-input" data-testid="radar-input" />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"><Send size={16} /></button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default OpportunityRadar;
